import { useState, useRef, type ChangeEvent } from "react";
import { createPortal } from "react-dom";
import { useUserProfile } from "@/contexts/UserProfileContext";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB

export function OnboardingModal() {
  const { updateProfile, uploadAvatar } = useUserProfile();
  const [username, setUsername] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select an image file (JPEG, PNG, WebP, etc.).");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError("Image must be under 2 MB.");
      return;
    }

    setError(null);
    setAvatarFile(file);
    const prev = URL.createObjectURL(file);
    setAvatarPreview(prev);
  };

  const handleSubmit = async () => {
    const trimmed = username.trim();
    if (trimmed.length < 3) {
      setError("Username must be at least 3 characters.");
      return;
    }
    if (trimmed.length > 30) {
      setError("Username must be 30 characters or fewer.");
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      let avatarUrl: string | undefined;
      if (avatarFile) {
        avatarUrl = await uploadAvatar(avatarFile);
      }
      await updateProfile(trimmed, avatarUrl ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-stone-900/60 p-4 backdrop-blur-sm">
      <div className="my-auto max-h-[calc(100vh-2rem)] w-full max-w-md overflow-y-auto rounded-3xl border border-stone-200 bg-white p-8 shadow-xl">
        <div className="mb-6 text-center">
          <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-stone-900 text-white text-xl font-bold">
            ↗
          </div>
          <h2 className="text-2xl font-semibold text-stone-900">Welcome to QuotedsArrow</h2>
          <p className="mt-2 text-sm text-stone-500">
            Choose a username to get started. Add a photo if you like.
          </p>
        </div>

        <div className="mb-5 flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="relative flex h-20 w-20 items-center justify-center rounded-full border-2 border-dashed border-stone-300 bg-stone-50 transition hover:border-stone-500 hover:bg-stone-100 overflow-hidden"
            title="Upload a profile photo"
          >
            {avatarPreview ? (
              <img
                src={avatarPreview}
                alt="Preview"
                className="h-full w-full rounded-full object-cover"
              />
            ) : (
              <span className="text-2xl text-stone-400 select-none">+</span>
            )}
          </button>
          <p className="text-xs text-stone-400">Optional photo · max 2 MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        <div className="mb-4">
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Username <span className="text-stone-400">(required)</span>
          </label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            placeholder="e.g. stoic_reader"
            maxLength={30}
            autoFocus
            className="w-full rounded-2xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-900 outline-none placeholder:text-stone-400 focus:border-stone-400 focus:ring-1 focus:ring-stone-400 transition"
          />
          <p className="mt-1 text-xs text-stone-400">{username.length}/30</p>
        </div>

        {error && (
          <div className="mb-4 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={isSubmitting || username.trim().length < 3}
          className="w-full rounded-2xl bg-stone-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-stone-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isSubmitting ? "Setting up…" : "Get started →"}
        </button>
      </div>
    </div>,
    document.body,
  );
}
